// Seasonal app icon + browser favicon. `null` season = default icon.
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { AppIcon } from "@capacitor-community/app-icon";
import { externalFlowActive } from "./externalFlow";

const ORIGINAL = "data-puzzly-original-href";
// Keys whose favicons/icons ship in the build (the released calendar is narrower).
const SHIPPED = ["christmas", "carnival", "easter", "valentine", "eid"];

export function iconNameFor(season) {
  return season ? `seasonal-${season}` : null;
}

export function applySeasonFavicon(season, doc = document) {
  const key = SHIPPED.includes(season) ? season : null;
  const base = process.env.PUBLIC_URL || "";
  doc.querySelectorAll('link[rel~="icon"]').forEach((link) => {
    const type = link.getAttribute("type") || "";
    const target = type === "image/svg+xml" ? `${base}/favicon-${key}.svg`
      : type === "image/png" ? `${base}/favicon-32-${key}.png` : null;
    if (key && target) {
      if (!link.hasAttribute(ORIGINAL)) link.setAttribute(ORIGINAL, link.getAttribute("href") || "");
      link.setAttribute("href", target);
    } else if (!key && link.hasAttribute(ORIGINAL)) {
      link.setAttribute("href", link.getAttribute(ORIGINAL));
      link.removeAttribute(ORIGINAL);
    }
  });
}

// Native alternate icon name: the iOS app icon set in Assets.xcassets and the
// Android <activity-alias android:name=".<name>">. Android component names must
// be Java identifiers, so "-" becomes "_" — this is the only place that mapping lives.
export function nativeIconName(season) {
  return SHIPPED.includes(season) ? iconNameFor(season).replace(/-/g, "_") : null;
}
//
// !! PERMANENT API — NEVER REMOVE OR RENAME AN ENTRY. !!
// On Android a switch disables MainActivity, and that state survives app updates.
// If a release drops or renames the alias a user currently has enabled, the app
// is left with ZERO launcher components and vanishes from the launcher. Removing
// a name requires a reset-only release (switches everyone back to the default)
// live for a full season first. Adding names is fine. Same list as the
// <activity-alias> entries in AndroidManifest.xml (checked by native-icons.test.js).
export const NATIVE_ICON_NAMES = SHIPPED.map(nativeIconName);

// Android: how long after `pause` the app must still be stopped before switching.
export const ANDROID_BACKGROUND_DELAY_MS = 5000;
// Cap on each plugin call, so a promise that never settles (e.g. the plugin's
// getName NPE when no launcher component is enabled) can't stall the queue.
export const PLUGIN_TIMEOUT_MS = 8000;

function withTimeout(promise, label) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} timed out after ${PLUGIN_TIMEOUT_MS} ms`)), PLUGIN_TIMEOUT_MS);
  });
  return Promise.race([Promise.resolve(promise), timeout]).finally(() => clearTimeout(timer));
}

// Puts the home-screen icon in line with `season`. No-op on web, and while an
// external flow (sign-in, purchase, picker, share) is open; only switches when the
// current icon differs. iOS shows its own system alert on every switch (accepted,
// spec §6). `disable` is required by the plugin on Android. Calls are queued so
// two overlapping triggers can't both see the old icon and switch twice.
let queue = Promise.resolve();
// `canSwitch` (optional, async) is re-checked right before change/reset: the
// Android scheduler uses it to re-read the app state, since the call can run
// seconds after the decision (queue + plugin timeouts).
export function syncNativeIcon(season, plugin = AppIcon, cap = Capacitor, { canSwitch } = {}) {
  if (!cap.isNativePlatform()) return Promise.resolve();
  queue = queue.then(() => switchIcon(nativeIconName(season), plugin, canSwitch));
  return queue;
}

async function switchIcon(want, plugin, canSwitch) {
  if (externalFlowActive()) return;
  try {
    const { value } = await withTimeout(plugin.getName(), "getName");
    if ((value || null) === want) return;
    if (externalFlowActive()) return;
    if (canSwitch && !(await withTimeout(canSwitch(), "canSwitch"))) return;
    if (want) {
      await withTimeout(plugin.change({
        name: want,
        suppressNotification: false,
        disable: NATIVE_ICON_NAMES.filter((n) => n !== want),
      }), "change");
    } else {
      await withTimeout(plugin.reset({ suppressNotification: false, disable: NATIVE_ICON_NAMES }), "reset");
    }
  } catch (err) {
    console.warn("[seasonIcon] native icon switch failed", err);
  }
}

// When to switch. Returns a cleanup function.
// iOS: only while the app is active — at start if already active, then on every
//   appStateChange isActive=true (didBecomeActive). Capacitor's `resume` is
//   willEnterForeground, where setAlternateIconName often fails, so it's not used.
// Android: only when the app is really in background. The switch disables the
//   component the running activity came from and the system finishes it, so on
//   `pause` wait ANDROID_BACKGROUND_DELAY_MS, cancel on resume / isActive=true, and
//   at the end switch only if the app is stopped (getState isActive=false, i.e.
//   onStop — a dialog on top only pauses) and no external flow is open.
//
// Off by default: REACT_APP_SEASON_ICON (build time) is a comma list of the
// platforms allowed to switch — "ios", "android", "ios,android". Unset or empty =
// no listeners and no plugin calls anywhere, so a native build made from main
// never touches the icon until a store build opts in.
export function seasonIconPlatforms(flag = process.env.REACT_APP_SEASON_ICON) {
  return String(flag || "").split(",").map((p) => p.trim().toLowerCase()).filter(Boolean);
}

export function watchNativeIcon(season, { plugin = AppIcon, app = CapApp, cap = Capacitor } = {}) {
  if (!cap.isNativePlatform()) return () => {};
  if (!seasonIconPlatforms().includes(cap.getPlatform())) return () => {};
  let stopped = false;
  const handles = [];
  const listen = (event, cb) => handles.push(Promise.resolve(app.addListener(event, cb)));
  const sync = (opts) => { if (!stopped) syncNativeIcon(season, plugin, cap, opts); };

  let timer = null;
  const cancel = () => { clearTimeout(timer); timer = null; };

  // Android: still stopped (onStop), no flow opened, watcher not cleaned up.
  const stillInBackground = async () => {
    if (stopped || externalFlowActive()) return false;
    const { isActive } = await app.getState();
    return !isActive;
  };

  if (cap.getPlatform() === "android") {
    listen("pause", () => {
      cancel();
      timer = setTimeout(async () => {
        timer = null;
        if (stopped || externalFlowActive()) return;
        try {
          const { isActive } = await app.getState();
          if (!isActive) sync({ canSwitch: stillInBackground });
        } catch (err) {
          console.warn("[seasonIcon] app state unavailable", err);
        }
      }, ANDROID_BACKGROUND_DELAY_MS);
    });
    listen("resume", cancel);
    listen("appStateChange", ({ isActive } = {}) => { if (isActive) cancel(); });
  } else {
    Promise.resolve(app.getState()).then(({ isActive } = {}) => { if (isActive) sync(); }, () => {});
    listen("appStateChange", ({ isActive } = {}) => { if (isActive) sync(); });
  }

  return () => {
    stopped = true;
    cancel();
    handles.forEach((h) => h.then((x) => x && x.remove()));
  };
}
